import { memo, useEffect, useRef, useState } from "react";
import styled from "styled-components";
import { Button, Tag } from "antd";
import { S2Mosaic_GetPage_Request, S2Mosaic_GetPage_ResponseItem, S2MosaicAPIs, S2MosaicType } from "~/common/typescript/apis/S2MosaicAPIs";
import S2MosaicItem from "./S2MosaicItem";
import S2MosaicFilterPanel from "./S2MosaicFilterPanel";
import { CloseCircleOutlined, EnvironmentOutlined } from "@ant-design/icons";
import { useAtom, useSetAtom } from 'jotai';
import { satelliteRecordCountAtom, satelliteFilterTriggerAtom, satelliteSearchPointAtom } from '../../store';


const S2MosaicFinder: React.FC = () => {
  const [s2mosaicList, setS2MosaicList] = useState<S2Mosaic_GetPage_ResponseItem[]>([]);

  const [year, setYear] = useState<number>(2025);
  const [type, setType] = useState<S2MosaicType>(S2MosaicType.Month);
  const [month, setMonth] = useState<number | undefined>(undefined);
  const [dateRange, setDateRange] = useState<[string, string] | [null, null]>([null, null]);
  const [searchPoint, setSearchPoint] = useState<{ lat: number; lng: number } | null>(null);

  const [filterVisible, setFilterVisible] = useState(false);
  const [satelliteSearchPoint] = useAtom(satelliteSearchPointAtom);
  const lastProcessedPointRef = useRef<number | null>(null);

  const fetchData = async (overridePoint?: { lat: number; lng: number } | null) => {
    const pt = overridePoint !== undefined ? overridePoint : searchPoint;
    const request: S2Mosaic_GetPage_Request = {
      type: type,
      year: year,
      month: month,
      start_date: dateRange[0] || undefined,
      end_date: dateRange[1] || undefined,
    };
    if (pt) {
      request.point = {
        type: 'Point',
        coordinates: [pt.lng, pt.lat],
      };
    }
    const data = await S2MosaicAPIs.remote_GetPage(request);
    setS2MosaicList(data);
    setFilterVisible(false); // Close the filter after search
  };
  
  const handleS2MosaicImageRefresh = (id: string, newWmsUrl: string) => {
    setS2MosaicList((prev) => prev.map((item) => item.id === id ? { ...item, wmsUrl: newWmsUrl } : item));
  };
  
  // React to search point from context menu
  useEffect(() => {
    if (
      satelliteSearchPoint &&
      satelliteSearchPoint.timestamp &&
      satelliteSearchPoint.timestamp !== lastProcessedPointRef.current
    ) {
      lastProcessedPointRef.current = satelliteSearchPoint.timestamp;
      const pt = { lat: satelliteSearchPoint.lat, lng: satelliteSearchPoint.lng };
      setSearchPoint(pt);
      fetchData(pt);
    }
  }, [satelliteSearchPoint]);

  // Initial load if no context search point is provided
  useEffect(() => {
    if (satelliteSearchPoint && satelliteSearchPoint.timestamp) {
      return;
    }
    fetchData();
  }, []);

  const handleClearPoint = () => {
    setSearchPoint(null);
    fetchData(null);
  };

  const setRecordCount = useSetAtom(satelliteRecordCountAtom);
  const setFilterTrigger = useSetAtom(satelliteFilterTriggerAtom);

  // Update record count when the list changes
  useEffect(() => {
    setRecordCount(s2mosaicList.length);
  }, [s2mosaicList, setRecordCount]);

  // Register the filter trigger function
  useEffect(() => {
    setFilterTrigger(() => {
      setFilterVisible(true);
    });
    return () => {
      setFilterTrigger(null);
    };
  }, [setFilterTrigger]);

  return (
    <>
      <UI_Wrapper>
        {searchPoint && (
          <div style={{ marginBottom: 8 }}>
            <Tag
              color="blue"
              icon={<EnvironmentOutlined />}
              closable
              onClose={handleClearPoint}
              style={{ fontSize: 13, padding: '4px 8px' }}
            >
              Lọc theo tọa độ: {searchPoint.lat.toFixed(4)}, {searchPoint.lng.toFixed(4)}
            </Tag>
          </div>
        )}
        {/* List panel */}
        <div style={{ flex: 1, overflowY: 'auto' }}>
          <UI_List_Wrapper>
            {s2mosaicList.map((item) => (
              <S2MosaicItem key={item.wmsUrl} item={item} onRefresh={handleS2MosaicImageRefresh} />
            ))}
          </UI_List_Wrapper>
        </div>
      </UI_Wrapper>

      {/* Filter popup */}
      <S2MosaicFilterPanel
        visible={filterVisible}
        onClose={() => setFilterVisible(false)}
        year={year}
        setYear={setYear}
        type={type}
        setType={setType}
        month={month}
        setMonth={setMonth}
        dateRange={dateRange}
        setDateRange={setDateRange}
        onSearch={() => fetchData()}
      />
    </>
  );
};

export default memo(S2MosaicFinder);

const UI_Wrapper = styled.div`
  padding: 8px;
  height: 100%;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
`;

const UI_List_Wrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
`;
